/**
 * src/stores/session.ts
 * -----------------------
 * Zustand store for session list management and active session tracking.
 *
 * Fully implemented this will:
 *   - Hold sessions[], activeSessionId, and isLoading flag.
 *   - loadSessions() — fetch from src/api/sessions.ts, populate store.
 *   - createNewSession(orgId) — calls createSession(), prepends to list,
 *     sets as active, clears chat store.
 *   - setActiveSession(id) — activates a session, triggers message load.
 *   - removeSession(id) — calls deleteSession() API, removes from list.
 *   - renameSession(id, title) — calls API, updates local state.
 *   - setAnonSessionId(id) — stores anon session ID for post-login merge.
 *
 * Key dependencies:
 *   zustand, src/api/sessions.ts, src/types/session.ts
 */
import { create } from 'zustand'
import type { Session } from '@/types/session'

interface SessionStore {
  sessions: Session[]
  activeSessionId: string | null
  anonSessionId: string | null
  isLoading: boolean

  loadSessions: (sessions: Session[]) => void
  setLoading: (v: boolean) => void
  prependSession: (session: Session) => void
  setActiveSession: (id: string) => void
  removeSession: (id: string) => void
  renameSession: (id: string, title: string) => void
  setAnonSessionId: (id: string | null) => void
}

export const useSessionStore = create<SessionStore>((set) => ({
  sessions: [],
  activeSessionId: null,
  anonSessionId: localStorage.getItem('helix_anon_session'),
  isLoading: false,

  loadSessions: (sessions) => set({ sessions }),

  setLoading: (v) => set({ isLoading: v }),

  prependSession: (session) =>
    set((s) => ({ sessions: [session, ...s.sessions] })),

  setActiveSession: (id) => set({ activeSessionId: id }),

  removeSession: (id) =>
    set((s) => ({
      sessions: s.sessions.filter((sess) => sess.id !== id),
      activeSessionId: s.activeSessionId === id ? null : s.activeSessionId,
    })),

  renameSession: (id, title) =>
    set((s) => ({
      sessions: s.sessions.map((sess) =>
        sess.id === id ? { ...sess, title } : sess,
      ),
    })),

  setAnonSessionId: (id) => {
    if (id) {
      localStorage.setItem('helix_anon_session', id)
    } else {
      localStorage.removeItem('helix_anon_session')
    }
    set({ anonSessionId: id })
  },
}))
